import { HttpBackend, HttpClient, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, finalize, map, Observable, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { AuthTokenService } from '../Services/auth-token.service';
import { ImpersonationFacade } from '../../../Facades/impersonation.facade';
import { AuthResponse, LaravelDefaultResponse } from '../Interfaces/response.interface';
import { environment } from '../../../../environments/environment';
import { endpoints } from '../../../../environments/endpoints';

let refreshRequest$: Observable<AuthResponse> | null = null;

function redirectToLogin(authTokenService: AuthTokenService, router: Router): void {
  authTokenService.setReturnUrl(router.url);
  authTokenService.clearToken();
  router.navigate(['/auth/login']);
}

function refreshAccessToken(
  http: HttpClient,
  authTokenService: AuthTokenService,
): Observable<AuthResponse> {
  if (!refreshRequest$) {
    const refresh_token = authTokenService.getRefreshToken();

    if (!refresh_token) {
      return throwError(() => new Error('No refresh token available.'));
    }

    refreshRequest$ = http.post<LaravelDefaultResponse<AuthResponse>>(
      `${environment.coreApi.host}/${endpoints.auth.refresh}`,
      { refresh_token },
      {
        headers: {
          'skip-loading': 'true'
        }
      }
    ).pipe(
      map(response => response.data),
      tap(auth_data => authTokenService.setToken(auth_data)),
      finalize(() => {
        refreshRequest$ = null;
      }),
      shareReplay(1)
    );
  }

  return refreshRequest$;
}

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const httpBackend = inject(HttpBackend);
  const authTokenService = inject(AuthTokenService);
  const impersonationFacade = inject(ImpersonationFacade);
  const router = inject(Router);
  const http = new HttpClient(httpBackend);

  const access_token = authTokenService.getToken();

  if (access_token && !authTokenService.isTokenExpired()) {
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${access_token}`
      }
    });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const is_unauthorized = error.status === 401;
      const is_refresh_call = req.url.includes('/auth/refresh');
      const is_logout_call = req.url.includes('/auth/logout');
      const has_refresh_token = authTokenService.hasRefreshToken();

      if (!is_unauthorized || is_logout_call) {
        return throwError(() => error);
      }

      if (authTokenService.isImpersonated()) {
        impersonationFacade.endSession();
        return throwError(() => error);
      }

      if (is_refresh_call || !has_refresh_token) {
        redirectToLogin(authTokenService, router);
        return throwError(() => error);
      }

      return refreshAccessToken(http, authTokenService).pipe(
        switchMap((auth_data) => {
          const retried_request = req.clone({
            setHeaders: {
              Authorization: `Bearer ${auth_data.access_token}`
            }
          });

          return next(retried_request);
        }),
        catchError((refresh_error) => {
          redirectToLogin(authTokenService, router);
          return throwError(() => refresh_error);
        })
      );
    })
  );
};
